import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  compose,
} from 'recompose';

import { Button, Icon } from 'semantic-ui-react';

import FeedTypeCards from './feed-type-cards';
import FeedTypeTable from './feed-type-table';

import { withDyzioEventsData, EVENT_TYPES } from '../../hocs/data/with-dyzio-events-data';
import {
  FEED_TYPES,
  FEED_VIEW_MODES,
} from './static';

const PER_PAGE = 8;
const DEBOUNCE_TIMEOUT = 200;
const TABLE_VIEW_MODE_PAGE_LIMIT = 100;
const TABLE_VIEW_MODE_COLLAB_LIMIT = 8;

const paginationStyle = {
  marginTop: 5,
  marginBottom: 10,
  float: 'right',
};

class EventsFeed extends Component {
  constructor() {
    super();
    this.state = {
      noEvents: false,
      isLoading: true,
      isLoadingMore: false,
      eventTypeFilter: EVENT_TYPES.ALL,
      eventSearchTerm: '',
      canLoadMore: true,
      hasFailedToLoad: false,
      events: [],
      currentOffset: 0,
    };
  }

  componentDidMount() {
    this.fetchEvents();
  }

  onFeedBodyScroll = _.debounce(({
    scrollHeight,
    scrollTop,
    clientHeight,
  }) => {
    const isBottom = scrollHeight - scrollTop === clientHeight;
    if (isBottom) {
      this.loadMore();
    }
  }, DEBOUNCE_TIMEOUT);

  onEventTypeFilterChanged = (event, data) => {
    const { value: eventTypeFilter } = data;
    this.setState({
      eventTypeFilter,
    }, this.fetchEvents);
  }

  onSearchByCreatedBy = value => this.setState({
    eventSearchTerm: value,
  }, this.fetchEvents);

  onClearSearch = () => this.setState({
    eventSearchTerm: '',
  }, this.fetchEvents);

  loadMore = () => {
    const { isLoadingMore, canLoadMore } = this.state;
    if (isLoadingMore || !canLoadMore) return;
    this.setState({
      isLoadingMore: true,
      currentOffset: _.add(this.state.currentOffset, PER_PAGE),
    }, this.fetchEvents);
  }

  onPaginationDecrease = () => {
    const { currentOffset } = this.state;
    if (currentOffset === 0) return;
    this.setState({
      currentOffset: _.subtract(currentOffset, PER_PAGE),
    }, this.fetchEvents);
  }

  onPaginationIncrease = () => {
    this.setState({
      currentOffset: _.add(this.state.currentOffset, PER_PAGE),
    }, this.fetchEvents);
  }

  fetchEvents = () => {
    const {
      getDyzioEvents,
      campaignId,
      organisationId,
      discussionId,
      feedType,
      viewMode,
    } = this.props;

    const {
      currentOffset,
      eventTypeFilter,
      isLoadingMore,
      eventSearchTerm,
    } = this.state;

    const campaignFilter = {
      campaign: campaignId,
      eventType: FEED_TYPES.CAMPAIGN,
    };

    const organisationFilter = {
      organisation: organisationId,
      eventType: FEED_TYPES.ORGANISATION,
    };

    const allFilter = {
      organisation: organisationId,
      eventName: [
        EVENT_TYPES.INFLUENCER_CREATED,
        EVENT_TYPES.USER_CREATED,
        EVENT_TYPES.CAMPAIGN_CREATED,
        EVENT_TYPES.SHORTLIST_CREATED,
        EVENT_TYPES.TRACKED_ASSED_CREATED,
      ],
    };

    const page = {
      limit: PER_PAGE,
      offset: currentOffset,
    };

    const shouldFilterByEventType = EVENT_TYPES.ALL !== eventTypeFilter;
    const shouldFetchAllEvents = FEED_TYPES.ALL === feedType;
    const shouldFilterByTerm = !_.isEmpty(eventSearchTerm);
    const isTableViewMode = viewMode === FEED_VIEW_MODES.TABLE;
    const isCollaborationMode = feedType === FEED_TYPES.COLLABORATION;

    if (shouldFilterByEventType) {
      _.assign(organisationFilter, { eventName: eventTypeFilter });
    }

    if (shouldFilterByTerm) {
      _.assign(organisationFilter, { createdBy: `:${eventSearchTerm}` });
    }

    if (isTableViewMode) {
      _.set(page, 'limit', isCollaborationMode ? TABLE_VIEW_MODE_COLLAB_LIMIT : TABLE_VIEW_MODE_PAGE_LIMIT);
    }

    const singleFilter = _.eq(FEED_TYPES.ORGANISATION, feedType) ? organisationFilter : campaignFilter;

    if (feedType === FEED_TYPES.COLLABORATION && discussionId) {
      _.assign(campaignFilter, { discussion: discussionId });
    }

    getDyzioEvents({
      filter: shouldFetchAllEvents ? allFilter : singleFilter,
      page,
    }).then(events => {
      const canLoadMore = _.size(events) === PER_PAGE;
      this.setState({
        events: isLoadingMore ? _.concat(this.state.events, events) : events,
        isLoading: false,
        isLoadingMore: false,
        canLoadMore,
        noEvents: _.isEmpty(events),
      });
      return events;
    }).catch(() => {
      this.setState({
        hasFailedToLoad: true,
        isLoadingMore: false,
        isLoading: false,
        noEvents: false,
        canLoadMore: false,
      });
    });
  }


  render() {
    const {
      isLoading,
      events,
      noEvents,
      isLoadingMore,
      canLoadMore,
      eventTypeFilter,
      currentOffset,
    } = this.state;

    const {
      isCompact,
      shouldRenderFilters,
      viewMode,
      hasExportFeature,
      csvFileName,
      feedType,
    } = this.props;

    const feedBodyProps = {
      events,
      isLoading,
      noEvents,
      isCompact,
      onFeedBodyScroll: this.onFeedBodyScroll,
    };

    const eventProps = {
      shouldRenderFilters,
      eventTypeFilter,
      canLoadMore,
      isLoadingMore,
      onEventTypeFilterChanged: this.onEventTypeFilterChanged,
      onSearch: this.onSearchByCreatedBy,
      onClearSearch: this.onClearSearch,
      ...this.props,
      ...feedBodyProps,
    };

    const isCollaborationMode = feedType === FEED_TYPES.COLLABORATION;

    const tableViewProps = {
      events,
      csvFileName,
      hasExportFeature,
      isLoading,
      isCollaborationMode,
    };

    const isCardsViewMode = viewMode === FEED_VIEW_MODES.CARDS;
    const isTableViewMode = viewMode === FEED_VIEW_MODES.TABLE;

    return (
      <div>
        {
          isCardsViewMode &&
          <FeedTypeCards {...eventProps} />
        }
        {
          isTableViewMode &&
          <FeedTypeTable {...tableViewProps} />
        }
        {
          isCollaborationMode &&
          <div>
            <Button.Group style={paginationStyle}>
              <Button disabled={currentOffset === 0} icon onClick={this.onPaginationDecrease}>
                <Icon name="angle left" />
              </Button>
              <Button icon onClick={this.onPaginationIncrease}>
                <Icon name="angle right" />
              </Button>
            </Button.Group>
          </div>
        }
      </div>
    );
  }
}

EventsFeed.propTypes = {
  getDyzioEvents: PropTypes.func.isRequired,
  campaignId: PropTypes.string,
  csvFileName: PropTypes.string,
  organisationId: PropTypes.string,
  shouldRenderFilters: PropTypes.bool,
  feedType: PropTypes.oneOf([...FEED_TYPES]).isRequired,
  viewMode: PropTypes.oneOf([...FEED_VIEW_MODES]),
  title: PropTypes.string,
  description: PropTypes.string,
  cardColour: PropTypes.string,
  isCompact: PropTypes.bool,
  hasExportFeature: PropTypes.bool,
};

EventsFeed.defaultProps = {
  title: 'Campaign Events',
  description: 'view the history of your automated channel report.',
  shouldRenderFilters: false,
  viewMode: FEED_VIEW_MODES.CARDS,
  isCompact: false,
  hasExportFeature: false,
};

export default compose(
  withDyzioEventsData,
)(EventsFeed);



/** WEBPACK FOOTER **
 ** ./src/containers/event-feed/index.js
 **/